import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useStake } from '../../../../hooks/useStake';
import { useTokenInfo } from '../../../../hooks/useTokenInfo';
import useWallet from '../../../../hooks/useWallet';
import Loader from '../../../../shared/Loader';
import TokenToDollor from '../../../../shared/TokenToDollor';
import { useTheme } from '../../../../hooks/ThemeProvider';
import { EXPLORER_URL } from '../../../../utils/constant';

function ClaimRewardsModal({ show, onHide, poolData, setDummyData }) {

    const { address } = useWallet();
    const [currentStep, setCurrentStep] = useState(1);
    const [transactionHash, setTransactionHash] = useState('');
    const [claimedAmount, setClaimedAmount] = useState('0');
    const [availableStakes, setAvailableStakes] = useState({
        amountETH: "0",
        restakeAmountETH: "0",
        claimCountdown: "00D:00H:00M",
        amountNumber: 0,
        restakeAmountNumber: 0,
        claimTime: null,
    });

    const [rewards, setRewards] = useState({
        rewardsETH: "0",
        rewardsNumber: 0,
        rewardsFormatted: "0"
    });

    const [gasEstimation, setGasEstimation] = useState({
        totalGasCostETH: 0,
        totalGasCostETHFormatted: "0"
    });

    const [countdown, setCountdown] = useState("00D:00H:00M");
    const [canClaim, setCanClaim] = useState(false);

    const { symbol } = useTokenInfo(poolData?.rootPool?.metadata.rewardToken);
    const { getAvailableStakes, getRewards, estimateClaimRewardsGas, claimRewards, isClaiming } = useStake();

    const shortenAddress = (address) => {
        if (!address) return 'N/A';
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    const truncateHash = (hash) => {
        if (!hash) return "0x74rhiw4rf38u...5r2";
        return `${hash.slice(0, 10)}...${hash.slice(-3)}`;
    };

    const handleClose = () => {
        setCurrentStep(1);
        setTransactionHash('');
        setClaimedAmount('0');
        onHide();
    };

    const handleClaim = async () => {
        if (!rewards.rewardsNumber || rewards.rewardsNumber === 0 || parseFloat(rewards.rewardsETH) === 0) {
            toast.error('No rewards available to claim');
            return;
        }

        if (!canClaim) {
            toast.error('Claim period has not started yet. Please wait for the countdown to complete.');
            return;
        }

        try {
            const { receipt, hash } = await claimRewards(poolData.contractAddress);

            if (receipt.status === 'success') {
                setTransactionHash(hash);
                setClaimedAmount(rewards.rewardsETH);
                setCurrentStep(2);
                setDummyData(`Rewards claimed successfully! #${Math.floor(Math.random() * 1000000)}`);

                await Promise.all([
                    fetchAvailableStakes(),
                    fetchRewards(),
                    fetchGasEstimation()
                ]);
            } else {
                toast.error('Transaction failed. Please try again.');
            }
        } catch (error) {
            console.error('Error claiming rewards:', error);

            if (error.message.includes('user rejected')) {
                toast.error('Transaction cancelled by user');
            } else if (error.message.includes('insufficient funds')) {
                toast.error('Insufficient funds for gas fees');
            } else {
                toast.error('Failed to claim rewards. Please try again.');
            }
        }
    };

    const fetchAvailableStakes = useCallback(async () => {
        if (!poolData?.contractAddress || !address) return;

        try {
            const stakes = await getAvailableStakes(poolData.contractAddress, address);
            setAvailableStakes({
                amountETH: stakes?.amountETH || "0",
                restakeAmountETH: stakes?.restakeAmountETH || "0",
                claimCountdown: stakes?.claimCountdown || "00D:00H:00M",
                amountNumber: stakes?.amountNumber || 0,
                restakeAmountNumber: stakes?.restakeAmountNumber || 0,
                claimTime: stakes?.claimTime || null,
            });
        } catch (error) {
            console.error("Failed to fetch available stakes:", error);
        }
    }, [poolData, address]);

    const fetchRewards = useCallback(async () => {
        if (!poolData?.contractAddress || !address) return;

        try {
            const rewardsData = await getRewards(poolData.contractAddress, address);
            setRewards({
                rewardsETH: rewardsData?.rewardsETH || "0",
                rewardsNumber: rewardsData?.rewardsNumber || 0,
                rewardsFormatted: rewardsData?.rewardsFormatted || "0"
            });
        } catch (error) {
            console.error("Failed to fetch rewards:", error);
        }
    }, [poolData, address]);

    const fetchGasEstimation = useCallback(async () => {
        if (!poolData?.contractAddress) return;

        try {
            const gasData = await estimateClaimRewardsGas(poolData.contractAddress);
            setGasEstimation({
                totalGasCostETH: gasData?.totalGasCostETH || 0,
                totalGasCostETHFormatted: gasData?.totalGasCostETHFormatted || "0"
            });
        } catch (error) {
            console.error("Failed to fetch gas estimation:", error);
        }
    }, [poolData]);

    useEffect(() => {
        if (!availableStakes.claimTime) {
            setCanClaim(false);
            return;
        }

        const updateCountdown = () => {
            const now = Math.floor(Date.now() / 1000);
            const remaining = availableStakes.claimTime - now;

            if (remaining <= 0) {
                setCountdown("00D:00H:00M");
                setCanClaim(true);
                return;
            }

            setCanClaim(false);
            const days = Math.floor(remaining / (3600 * 24));
            const hours = Math.floor((remaining % (3600 * 24)) / 3600);
            const minutes = Math.floor((remaining % 3600) / 60);

            setCountdown(
                `${String(days).padStart(2, '0')}D:${String(hours).padStart(2, '0')}H:${String(minutes).padStart(2, '0')}M`
            );
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [availableStakes.claimTime]);

    useEffect(() => {
        fetchAvailableStakes();
        fetchRewards();
        fetchGasEstimation();
    }, [fetchAvailableStakes, fetchRewards, fetchGasEstimation]);

    const isDisabled = !canClaim || !rewards.rewardsNumber || rewards.rewardsNumber === 0 || parseFloat(rewards.rewardsETH) === 0 || isClaiming;

    const getLoadingText = () => {
        if (isClaiming) return 'Claiming Rewards...';
        return 'Loading...';
    };
 const { theme } = useTheme();
    return (
        <>
            {isClaiming && <Loader loadingText={getLoadingText()} />}

            {/* Step 1: Claim Rewards Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 1 && !isClaiming}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Claim Rewards</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="modal_share">
                        <div className="inner_type">
                            <div className="one_modal">
                                <p>Claimable Rewards</p>
                                <h3>{rewards.rewardsETH} {symbol}</h3>
                                <p className="numbers">~<TokenToDollor tokenAddress={poolData?.rootPool?.metadata?.rewardToken} tokenAmount={rewards?.rewardsETH || '0'} /></p>
                            </div>

                            <div className="main_lock">
                                <div className="inner_lock">
                                    <p>Pool Address</p>
                                    <span>{poolData ? shortenAddress(poolData.contractAddress) : 'N/A'}</span>
                                </div>
                                <div className="inner_lock">
                                    <p>Creator</p>
                                    <span>{shortenAddress(poolData?.creator?.walletAddress) || 'Unknown'}</span>
                                </div>
                                <div className="inner_lock">
                                    <p>Claim available in</p>
                                    <span className={canClaim ? "text-success" : "text-warning"}>
                                        {canClaim ? "00D:00H:00M" : countdown}
                                    </span>
                                </div>
                                <div className="inner_lock">
                                    <p>Gas Fees</p>
                                    <span><TokenToDollor tokenAddress={''} tokenAmount={gasEstimation.totalGasCostETHFormatted || '0'} /> ({gasEstimation.totalGasCostETHFormatted} ETH)</span>
                                </div>
                            </div>
                        </div>

                        <div className="buton_modal_div">
                            <button
                                onClick={handleClaim}
                                disabled={isDisabled}
                                style={{
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isClaiming ? 'Claiming...' : 'Claim Rewards'}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 2: Success Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 2 && !isClaiming}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="succes_modal">
                        <div className="top_success">
                            <img src="/assets/tick.svg" alt="success" />
                            <h3>Rewards Claimed Successfully!</h3>
                            <p>
                                You've claimed <strong>{claimedAmount} {symbol}</strong> from this pool.
                            </p>
                        </div>
                        <div className="hash_div">
                            <div>
                                <p>Transaction Hash</p>
                                <span>{truncateHash(transactionHash)}</span>
                            </div>
                            <button
                                onClick={() => window.open(`${EXPLORER_URL}${transactionHash}`, '_blank')}
                            >
                                      {theme === 'dark' ? (
                            <img src="/assets/whitetab.svg" alt="view" />
                    ) : (
                     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
  <path d="M12.25 8.08333V11.5833C12.25 11.8928 12.1271 12.1895 11.9083 12.4083C11.6895 12.6271 11.3928 12.75 11.0833 12.75H2.91667C2.60725 12.75 2.3105 12.6271 2.09171 12.4083C1.87292 12.1895 1.75 11.8928 1.75 11.5833V3.41667C1.75 3.10725 1.87292 2.8105 2.09171 2.59171C2.3105 2.37292 2.60725 2.25 2.91667 2.25H6.41667" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.25 2.25L7 7.5" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.75 2.25H12.25V5.75" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                    )}
                                View
                            </button>
                        </div>
                        <div className="buton_modal_div">
                            <button onClick={handleClose}>Close</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ClaimRewardsModal;